import React, { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { loadUser } from '@/store/slices/authSlice';
import { AppDispatch } from '@/store';
import BuyerDashboard from '@/pages/buyer/BuyerDashboard';
import SellerDashboard from '@/pages/seller/SellerDashboard';
import AdminDashboard from '@/pages/admin/AdminDashboard';

const DashboardPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user, isAuthenticated, isLoading, isSuperAdmin, isAdmin, isStaff, isUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [role, setRole] = useState<string>('');
  const [hasTriedLoadUser, setHasTriedLoadUser] = useState(false);

  useEffect(() => {
    // Only redirect to login if we're sure user is not authenticated
    // Don't redirect if we're still loading or if we have a token
    if (!isLoading && !isAuthenticated) {
      const token = localStorage.getItem('token');
      // If there's a token, try loading user first before redirecting
      if (token && !hasTriedLoadUser) {
        setHasTriedLoadUser(true);
        dispatch(loadUser()).catch(() => {
          // If load fails, redirect to login
          navigate('/auth/login', { replace: true });
        });
        return;
      }
      // No token, redirect to login
      if (!token) {
        navigate('/auth/login', { replace: true });
      }
    }
  }, [navigate, isAuthenticated, isLoading, dispatch, hasTriedLoadUser]);

  // Helper function to check if a role is allowed for the current user
  const isRoleAllowed = (roleToCheck: string): boolean => {
    if (roleToCheck === 'super_admin') return isSuperAdmin();
    if (roleToCheck === 'admin') return isAdmin() || isSuperAdmin();
    if (roleToCheck === 'staff') return isStaff() || isAdmin() || isSuperAdmin();
    if (roleToCheck === 'seller') return isUser() || isAdmin() || isSuperAdmin();
    if (roleToCheck === 'buyer') return isUser();
    return false;
  };

  useEffect(() => {
    if (!user) return;
    
    // If role is already set and matches URL, don't do anything
    const currentRoleParam = searchParams.get('role');
    if (role && currentRoleParam === role && isRoleAllowed(role)) {
      return;
    }

    // Get role from URL query parameter
    let urlRole = currentRoleParam;
    
    // Validate role from URL - if not allowed, ignore it
    if (urlRole && !isRoleAllowed(urlRole)) {
      urlRole = null;
    }
    
    // If no role in query params, check localStorage
    if (!urlRole) {
      const savedRole = localStorage.getItem('selectedRole');
      if (savedRole && isRoleAllowed(savedRole)) {
        urlRole = savedRole;
      }
    }
    
    // If still no role, infer from URL path (but validate it)
    if (!urlRole) {
      if (location.pathname.startsWith('/dashboard/super-admin')) {
        urlRole = isSuperAdmin() ? 'super_admin' : null;
      } else if (location.pathname.startsWith('/dashboard/admin')) {
        urlRole = (isAdmin() || isSuperAdmin()) ? 'admin' : null;
      } else if (location.pathname.startsWith('/dashboard/staff')) {
        urlRole = (isStaff() || isAdmin() || isSuperAdmin()) ? 'staff' : null;
      } else if (location.pathname.startsWith('/dashboard/seller')) {
        urlRole = (isUser() || isAdmin() || isSuperAdmin()) ? 'seller' : null;
      } else if (location.pathname.startsWith('/dashboard/buyer')) {
        urlRole = isUser() ? 'buyer' : null;
      }
    }
    
    // If still no role, determine from user's actual role
    // For regular users, default to 'buyer' role
    if (!urlRole) {
      if (isSuperAdmin()) urlRole = 'super_admin';
      else if (isAdmin()) urlRole = 'admin';
      else if (isStaff()) urlRole = 'staff';
      else if (isUser()) {
        // Default to buyer for regular users
        urlRole = 'buyer';
        // Set it in localStorage as default
        localStorage.setItem('selectedRole', 'buyer');
      }
    }
    
    // Update role state if we have a valid role (even if it's the same, to ensure it's set)
    if (urlRole && isRoleAllowed(urlRole)) {
      // Always set the role if it's valid, even if it's the same
      // This ensures the role is set on initial load
      if (urlRole !== role) {
        setRole(urlRole);
      } else if (!role) {
        // If role state is empty but we have a valid urlRole, set it
        setRole(urlRole);
      }
      
      // Only navigate if URL doesn't already have the role parameter
      // and we're not already on a role-specific path
      if (!currentRoleParam && location.pathname === '/dashboard' && urlRole !== role) {
        navigate(`/dashboard?role=${urlRole}`, { replace: true });
      }
    }
    // Note: isRoleAllowed is not in dependencies because it's defined in the component
    // and only uses the functions already in the dependency array (isSuperAdmin, isAdmin, etc.)
  }, [user, searchParams, location.pathname, isSuperAdmin, isAdmin, isStaff, isUser, navigate, role]);

  // Check if we have a token - if we do, we should wait for user to load
  const hasToken = localStorage.getItem('token');
  
  // Show loading if:
  // 1. We're currently loading, OR
  // 2. We have a token but no user yet (waiting for user to load)
  if (isLoading || (hasToken && !user && !hasTriedLoadUser)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading user data...</p>
        </div>
      </div>
    );
  }

  // Only show "Not authenticated" if we're sure there's no token and no user
  // Don't show this if we have a token (even if user load failed, let the redirect handle it)
  if (!hasToken && !isAuthenticated && !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Not authenticated. Redirecting to login...</p>
        </div>
      </div>
    );
  }

  // If we have a valid role set, use it
  if (role && isRoleAllowed(role)) {
    if (role === 'super_admin' || role === 'admin' || role === 'staff') {
      return <AdminDashboard />;
    } else if (role === 'seller') {
      return <SellerDashboard />;
    } else if (role === 'buyer') {
      return <BuyerDashboard />;
    }
  }
  
  // If role is not set yet or invalid, determine correct role based on user's actual permissions
  // This ensures we always render something, even if role state hasn't been set yet
  if (user) {
    if (isSuperAdmin()) {
      return <AdminDashboard />;
    } else if (isAdmin()) {
      return <AdminDashboard />;
    } else if (isStaff()) {
      return <AdminDashboard />;
    } else if (isUser()) {
      return <BuyerDashboard />;
    }
  }

  // Default fallback - show loading if we have a user but can't determine role
  // This should rarely happen, but provides a safety net
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading dashboard...</p>
      </div>
    </div>
  );
};

export default DashboardPage;
